import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DContext } from '../../../context/DataContext';
import { BsTrash } from 'react-icons/bs';
import AdminListFooter from '../../blocks/AdminListFooter';

function VideoAdUpload() {
    const { t } = useTranslation();
    const { BeURL } = useContext(DContext);

    const [videos, setVideos] = useState([]);
    const [videoLink, setVideoLink] = useState('');
    const [targetId, setTargetId] = useState('');
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState('');
    const [togglingId, setTogglingId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const hasActiveVideo = useMemo(() => videos.length > 0, [videos.length]);
    const isReplacing = targetId !== '';
    const totalItems = videos.length;
    const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
    const paginatedVideos = videos.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const fetchVideos = useCallback(async () => {
        if (!BeURL) {
            return;
        }

        try {
            const res = await fetch(`${BeURL}/fetch-ad-videos`, {
                credentials: 'include'
            });
            const data = await res.json();
            if (data.success) {
                setVideos(Array.isArray(data.videos) ? data.videos : []);
                if (data.videos?.length > 0 && !targetId) {
                    setTargetId(data.videos[0]._id);
                }
            }
        } catch (error) {
            console.log('Error fetching ad videos:', error);
        }
    }, [BeURL, targetId]);

    useEffect(() => {
        if (BeURL) {
            fetchVideos();
        }
    }, [BeURL, fetchVideos]);

    const getYoutubeEmbedUrl = (value) => {
        const raw = String(value || '').trim();
        if (!raw) return null;
        const regexes = [
            /(?:youtube\.com\/watch\?v=|youtube\.com\/shorts\/|youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
            /[?&]v=([a-zA-Z0-9_-]{11})/
        ];

        for (const regex of regexes) {
            const match = raw.match(regex);
            if (match && match[1]) {
                return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
            }
        }

        return null;
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!videoLink.trim()) {
            setMessage(t('videoAd.enterYoutubeLink', { defaultValue: 'Please enter a YouTube video link.' }));
            return;
        }

        if (!getYoutubeEmbedUrl(videoLink)) {
            setMessage(t('videoAd.invalidYoutubeLink', { defaultValue: 'Please enter a valid YouTube URL.' }));
            return;
        }

        try {
            setUploading(true);
            const endpoint = isReplacing ? `/replace-ad-video/${targetId}` : '/upload-ad-video';
            const method = isReplacing ? 'PUT' : 'POST';

            const res = await fetch(`${BeURL}${endpoint}`, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: videoLink.trim() })
            });

            const data = await res.json();

            setUploading(false);

            if (data.success) {
                setMessage(isReplacing ? t('videoAd.replaceSuccess') : t('videoAd.success'));
                setVideoLink('');
                setTargetId('');
                await fetchVideos();
            } else {
                if (String(data.message || '').toLowerCase().includes('login')) {
                    setMessage(t('auth.messages.loginRequired'));
                    window.location.href = '/login';
                    return;
                }
                setMessage(data.message || (isReplacing ? t('videoAd.replaceFailure') : t('videoAd.failure')));
            }
        } catch (err) {
            console.error(err);
            setUploading(false);
            setMessage(t('videoAd.serverError'));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('videoAd.confirmDelete'))) {
            return;
        }

        try {
            setDeletingId(id);
            const res = await fetch(`${BeURL}/delete-ad-video/${id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await res.json();
            setDeletingId('');

            if (data.success) {
                setMessage(t('videoAd.deleteSuccess'));
                setTargetId('');
                await fetchVideos();
            } else {
                if (String(data.message || '').toLowerCase().includes('login')) {
                    setMessage(t('auth.messages.loginRequired'));
                    window.location.href = '/login';
                    return;
                }
                setMessage(data.message || t('videoAd.deleteFailure'));
            }
        } catch (error) {
            console.log('Error deleting ad video:', error);
            setDeletingId('');
            setMessage(t('videoAd.deleteServerError'));
        }
    };

    const handleToggle = async (id, currentStatus) => {
        try {
            setTogglingId(id);
            const res = await fetch(`${BeURL}/toggle-ad-video/${id}`, {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            setTogglingId('');

            if (data.success || data.successful) {
                setMessage(data.message);
                await fetchVideos();
            } else {
                if (String(data.message || '').toLowerCase().includes('login')) {
                    setMessage(t('auth.messages.loginRequired'));
                    window.location.href = '/login';
                    return;
                }
                setMessage(data.message || t('videoAd.toggleFailure'));
            }
        } catch (error) {
            console.log('Error toggling ad video:', error);
            setTogglingId('');
            setMessage(t('videoAd.toggleServerError'));
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-extrabold tracking-tight text-[#1739bd]">{t('videoAd.heading')}</h2>
                <p className="mt-1 text-sm text-slate-600">{t('videoAd.description')}</p>
            </div>

            {message && (
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
                    {message}
                </div>
            )}

            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
                <h3 className="text-base font-bold text-slate-900">
                    {isReplacing ? t('videoAd.replaceTitle') : t('videoAd.uploadTitle')}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                    {isReplacing
                        ? t('videoAd.replaceDescription')
                        : t('videoAd.uploadDescription')}
                </p>

                <form onSubmit={handleUpload} className="mt-4 space-y-4">
                    {hasActiveVideo && (
                        <div>
                            <label htmlFor="videoSelect" className="block text-sm font-medium text-slate-700 mb-1">
                                {t('videoAd.selectToReplace')}
                            </label>
                            <select
                                id="videoSelect"
                                className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
                                value={targetId}
                                onChange={(e) => setTargetId(e.target.value === '' ? '' : e.target.value)}
                            >
                                <option value="">{t('videoAd.uploadNewInstead')}</option>
                                {videos.map((video) => (
                                    <option key={video._id} value={video._id}>
                                        {t('videoAd.videoIdOption', { id: video._id.slice(0, 8) })}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label htmlFor="videoLink" className="block text-sm font-medium text-slate-700 mb-1">
                            {t('videoAd.youtubeLink', { defaultValue: 'YouTube Link' })}
                        </label>
                        <input
                            id="videoLink"
                            type="url"
                            className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
                            placeholder="https://www.youtube.com/watch?v=..."
                            value={videoLink}
                            onChange={(e) => setVideoLink(e.target.value)}
                        />
                    </div>

                    {getYoutubeEmbedUrl(videoLink) && (
                        <div className="rounded-2xl border border-slate-200 p-2">
                            <iframe
                                src={getYoutubeEmbedUrl(videoLink)}
                                title="YouTube preview"
                                className="h-52 w-full rounded-xl"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        className="inline-flex w-full items-center justify-center rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70"
                        disabled={uploading}
                    >
                        {uploading
                            ? t('videoAd.processing')
                            : isReplacing
                                ? t('videoAd.replaceButton')
                                : t('videoAd.uploadButton')}
                    </button>
                </form>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_14px_34px_rgba(15,23,42,0.06)] sm:p-5">
                <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-base font-bold text-slate-900">{t('videoAd.libraryTitle')}</h3>
                    <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-[#1739bd]">{t('videoAd.videosCount', { count: videos.length })}</span>
                </div>

                {videos.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">{t('videoAd.noVideo')}</p>
                ) : (
                    <div className="space-y-3">
                        {paginatedVideos.map((video) => (
                            <article key={video._id} className="rounded-2xl border border-slate-200 p-3">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('videoAd.videoId')}</p>
                                        <p className="truncate text-sm text-slate-700">{video._id}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="relative inline-flex cursor-pointer items-center">
                                            <input
                                                type="checkbox"
                                                className="peer sr-only"
                                                checked={video.enabled}
                                                onChange={() => handleToggle(video._id, video.enabled)}
                                                disabled={togglingId === video._id}
                                            />
                                            <div className="peer relative h-7 w-12 rounded-full bg-slate-300 after:absolute after:top-0.5 after:left-0.5 after:h-6 after:w-6 after:rounded-full after:bg-white after:transition-all peer-checked:bg-emerald-500 peer-checked:after:translate-x-5 peer-disabled:opacity-50"></div>
                                            <span className="ml-2 text-xs font-medium text-slate-600">
                                                {togglingId === video._id ? t('videoAd.toggling') : video.enabled ? t('videoAd.enabled') : t('videoAd.disabled')}
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {getYoutubeEmbedUrl(video.path) ? (
                                    <iframe
                                        src={getYoutubeEmbedUrl(video.path)}
                                        title={`YouTube ad ${video._id}`}
                                        className="mb-3 h-56 w-full rounded-xl border border-slate-200"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                    />
                                ) : (
                                    <video src={video.path} controls className="max-h-80 w-full rounded-xl border border-slate-200 object-cover mb-3" />
                                )}

                                <button
                                    type="button"
                                    onClick={() => handleDelete(video._id)}
                                    className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-60"
                                    disabled={deletingId === video._id}
                                >
                                    <BsTrash />
                                    {deletingId === video._id ? t('videoAd.deleting') : t('videoAd.delete')}
                                </button>
                            </article>
                        ))}
                    </div>
                )}

                <AdminListFooter
                    totalItems={totalItems}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={(value) => {
                        setItemsPerPage(value);
                        setCurrentPage(1);
                    }}
                    onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                    onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                />
            </section>
        </div>
    );
}

export default VideoAdUpload;